import { Component, HostListener, OnInit } from '@angular/core';
import { EnergyDataService } from '../../_shared/services/energy-data.service';
import { Globals } from '../../_shared/services/globals';
import { MonitoringService } from '../../_shared/services/monitoring.service';

interface ProfileData {
    date: string;
    value: string;
}

interface InputSeries {
    data: number[];
    color: string;
    title: string;
}

interface ZoomData {
    depth: number,
    label: string
}

@Component({
    selector: 'mobile-energy',
    templateUrl: './mobile-energy.component.html',
    styleUrls: ['./mobile-energy.component.css']
})
export class MobileEnergyComponent implements OnInit {
    monthData = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];

    // Switches
    showingGas: boolean = false;
    showingMyEnergy: boolean = false;

    showingActualChart: boolean = false;
    showingHistoryChart: boolean = false;
    
    // basis for calculation and Chart series
    energyData: number[] = [];
    solarData: number[] = [];
    gasData: number[] = [];

    currentDate: Date = new Date();
    
    // Mobile numbers
    yearBalance: number = 0;
    dayBalance: number = 0;
    totalUsage: number = 0;
    totalProduction: number = 0;
    dayUsage: number = 0;
    dayProduction: number = 0;
    actualEnergy: number = 0;
    actualSolar: number = 0;

    // Chart series
    daySolar: number[] = [];
    dayEnergy: number[] = [];
    yearSolar: number[] = [];
    yearEnergy: number[] = [];
    yearGas: number[] = [];
    dayGas: number[] = [];

    chartTitle: string = "";
    actualChartData: InputSeries[] = [];
    historyChartData: InputSeries[] = [];
    
    hourLabels: string[] = [];
    monthLabels = ["Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"];

    constructor(public globals: Globals, private dataService: EnergyDataService,
        monitoringService: MonitoringService) {
        monitoringService.logPageView();
        this.initLabels();        
    }

    ngOnInit(): void {
        this.dataService.dataLoadedEvent.subscribe((dataset: string) => this.refreshData());
        this.refreshData();
    }

    // Aggregation
    aggregationToYear(jsonData: ProfileData[]): number[] {
        var result: number[] = new Array(12);
        for (let i = 0; i < 12; i++) {
            result[i] = 0;
        }
        if (jsonData == null || jsonData.length == 0) {
            return result;
        }
        for (let d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() < this.currentDate.getMonth() || (date.getMonth() == this.currentDate.getMonth() && date.getDate() <= this.currentDate.getDate())) {
                result[date.getMonth()] += this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
            }
        }

        return result;
    }

    aggregationToMonth(jsonData: ProfileData[], targetMonth: number): number[] {
        var result: number[] = new Array(new Date(2017, targetMonth + 1, 0).getDate()), dataFound: boolean = false;
        for (var i = 0; i < new Date(2017, targetMonth + 1, 0).getDate(); i++) {
            result[i] = 0;
        }
        
        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() == targetMonth && (targetMonth < this.currentDate.getMonth() || targetMonth == this.currentDate.getMonth() && date.getDate() < this.currentDate.getDate() || targetMonth == this.currentDate.getMonth() && date.getDate() == this.currentDate.getDate() && date.getHours() < this.currentDate.getHours())) {
                result[date.getDate() - 1] += this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
                dataFound = true;
            } else if (dataFound) {
                break;
            }
        }
        
        return result;
    }

    aggregationToDay(jsonData: ProfileData[], targetMonth: number, targetDay: number): number[] {
        var result: number[] = new Array(24), dataFound: boolean = false;
        for (var i = 0; i < 24; i++) {
            result[i] = 0;
        }

        if (jsonData == null || jsonData.length == 0) {
            return result;
        }
        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() == targetMonth && date.getDate() == targetDay && (targetMonth < this.currentDate.getMonth() || targetMonth == this.currentDate.getMonth() && date.getDate() < this.currentDate.getDate() || targetMonth == this.currentDate.getMonth() && date.getDate() == this.currentDate.getDate() && date.getHours() < this.currentDate.getHours())) {
                result[date.getHours()] += this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
                dataFound = true;
            } else if (dataFound) {
                break;
            }
        }

        return result;
    }

    // Returns one data value; the latest one
    // NOTE: assuming hourly data
    aggregationToNow(jsonData: ProfileData[]) {
        if (jsonData == null || jsonData.length == 0) {
            return 0;
        }
        let currentDate = new Date();
        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if (date.getMonth() == currentDate.getMonth() && date.getDate() == currentDate.getDate() && date.getHours() >= currentDate.getHours()) {
                return this.globals.dataInEur ? parseFloat(d.value) * this.globals.energyPrice : parseFloat(d.value);
            }
        }
        return 0;
    }

    getSeriesTotal(series: number[]): number {
        let result: number = 0;
        for (let i = 0; i < series.length; i++) {
            result += series[i];
        }

        return result;
    }

    getBalance(production: number[], consumption: number[]) {
        var balanceResult = [];
        if (production == null || production.length == 0) {
            for (let val of consumption) {
                balanceResult.push(val * -1);
            }
        } else {
            for (let i = 0; i < production.length && i < consumption.length; i++) {
                balanceResult.push(production[i] - consumption[i]);
            }
        }
        return balanceResult;
    }


    @HostListener('window:refreshData')
    refreshData() {
        if (this.showingGas) {
            this.loadMobileGasBlocks();
        }
        else {
            this.loadMobileEnergyBlocks();
        }
    }

    getMonthId(targetMonth: string): number {
        var result = -1;

        for (var i = 0; i < this.monthData.length; i++) {
            if (this.monthData[i] == targetMonth) {
                result = i;
                break;
            }
        }

        return result;
    }
    
    loadMobileEnergyBlocks() {
        let energySource = this.dataService.energyProfileData;
        let solarSource = this.dataService.solarProfileData;
        let gasSource = this.dataService.gasProfileData;
        if (!this.globals.demoData) {
            if (this.showingMyEnergy) {
                energySource = this.dataService.energyActualData;
                solarSource = this.dataService.solarActualData;
                gasSource = this.dataService.gasActualData;
            }
            else {
                energySource = this.dataService.islandEnergyActual;
                solarSource = this.dataService.islandSolarActual;
                // TODO: retrieve actual gas for island
                gasSource = this.dataService.gasActualData;
            }
        }
        else {
            if (!this.showingMyEnergy) {
                energySource = this.dataService.islandEnergyProfile;
                solarSource = this.dataService.islandSolarProfile;
                // TODO: retrieve demo gas for island
                gasSource = this.dataService.gasActualData;
            }
        }
        if (energySource && solarSource) {
            let today = new Date();
            this.yearEnergy = this.aggregationToYear(energySource);
            this.yearSolar = this.aggregationToYear(solarSource);
            this.dayEnergy = this.aggregationToDay(energySource, today.getMonth(), today.getDate());
            this.daySolar = this.aggregationToDay(solarSource, today.getMonth(), today.getDate());

            this.actualEnergy = this.aggregationToNow(energySource);
            this.actualSolar = this.aggregationToNow(solarSource);

            this.totalUsage = this.getSeriesTotal(this.yearEnergy);
            this.totalProduction = this.getSeriesTotal(this.yearSolar);
            this.dayUsage = this.getSeriesTotal(this.dayEnergy);
            this.dayProduction = this.getSeriesTotal(this.daySolar);

            this.yearBalance = this.totalProduction - this.totalUsage;
            this.dayBalance = this.dayProduction - this.dayUsage;
        }
    }

    loadMobileGasBlocks() {
        let gasSource = this.dataService.gasProfileData;
        if (!this.globals.demoData) {
            gasSource = this.dataService.gasActualData;
        }
        // TODO: load gas data for island
        if (gasSource) {
            let today = new Date();
            this.yearGas = this.aggregationToYear(gasSource);
            this.dayGas = this.aggregationToDay(gasSource, today.getMonth(), today.getDate());

            this.actualEnergy = this.aggregationToNow(gasSource);
            this.actualSolar = 0;

            this.totalUsage = this.getSeriesTotal(this.yearGas);
            this.totalProduction = 0;
            this.dayUsage = this.getSeriesTotal(this.dayGas);
            this.dayProduction = 0;

            this.yearBalance = this.totalProduction - this.totalUsage;
            this.dayBalance = this.dayProduction - this.dayUsage;
        }
    }

    switchUnit() {
        this.globals.dataInEur = !this.globals.dataInEur;
        this.globals.refreshData();
    }

    switchSource() {
        this.showingMyEnergy = !this.showingMyEnergy;
        this.refreshData();
    }

    switchDataType() {
        this.showingGas = !this.showingGas;
        this.refreshData();
    }

    switchDemoData() {
        this.globals.demoData = !this.globals.demoData;
        this.globals.refreshData();
    }

    showActualChart(type: string) {
        this.showingActualChart = true;
        if (!this.showingGas) {
            if (type == 'consumption') {
                this.chartTitle = "Energie verbruik";
                this.actualChartData = [
                    {
                        data: this.dayEnergy,
                        color: "#a1c4d0",
                        title: "Energie verbruik vandaag"
                    }
                ];
            }
            else if (type == 'production') {
                this.chartTitle = "Energie opgewekt";
                this.actualChartData = [
                    {
                        data: this.daySolar,
                        color: "#f2a200",
                        title: "Energie opgewekt vandaag"
                    }
                ];
            }
            else if (type == 'balance') {
                this.chartTitle = "Energie besparing";
                this.actualChartData = [
                    {
                        data: this.getBalance(this.daySolar, this.dayEnergy),
                        color: "#f2a200",
                        title: "Energie besparing vandaag"
                    }
                ];
            }
        }
        else {
            if (type == 'consumption') {
                this.chartTitle = "Gas verbruik";
                this.actualChartData = [
                    {
                        data: this.dayGas,
                        color: "#a1c4d0",
                        title: "Gas verbruik vandaag"
                    }
                ];
            }
            else if (type == 'balance') {
                this.chartTitle = "Gas balans";
                this.actualChartData = [
                    {
                        data: this.getBalance([], this.dayGas),
                        color: "#f2a200",
                        title: "Gas balans vandaag"
                    }
                ];
            }
            else {
                this.chartTitle = "Gas";
                this.actualChartData = []
            }
        }
    }

    showHistoryChart(type: string) {
        this.showingHistoryChart = true;
        if (!this.showingGas) {
            if (type == 'consumption') {
                this.chartTitle = "Energie verbruik";
                this.historyChartData = [
                    {
                        data: this.yearEnergy,
                        color: "#a1c4d0",
                        title: "Energie verbruik dit jaar"
                    }
                ];
            }
            else if (type == 'production') {
                this.chartTitle = "Energie opgewekt";
                this.historyChartData = [
                    {
                        data: this.yearSolar,
                        color: "#f2a200",
                        title: "Energie opgewekt dit jaar"
                    }
                ];
            }
            else if (type == 'balance') {
                this.chartTitle = "Energie besparing";
                this.historyChartData = [
                    {
                        data: this.getBalance(this.yearEnergy, this.yearSolar),
                        color: "#f2a200",
                        title: "Energie besparing dit jaar"
                    }
                ];
            }
        }
        else {
            if (type == 'consumption') {
                this.chartTitle = "Gas verbruik";
                this.historyChartData = [
                    {
                        data: this.yearGas,
                        color: "#a1c4d0",
                        title: "Gas verbruik dit jaar"
                    }
                ];
            }
            else if (type == 'balance') {
                this.chartTitle = "Gas balans";
                this.historyChartData = [
                    {
                        data: this.getBalance([], this.yearGas),
                        color: "#f2a200",
                        title: "Gas balans dit jaar"
                    }
                ];
            }
            else {
                this.chartTitle = "Gas";
                this.historyChartData = []
            }
        }
    }

    showOverview() {
        this.showingActualChart = false;
        this.showingHistoryChart = false;
    }

    initLabels() {        
        this.hourLabels = [];
        for (let i = 0; i <= 24; i++) {
            let hour = i + this.currentDate.getHours();
            hour = hour > 23 ? hour - 24 : hour;
            let label = hour < 10 ? "0" + hour : "" + hour;
            label += ":00";
            this.hourLabels.push(label);
        }
    }
}



// WEBPACK FOOTER //
// ./ClientApp/app/pages/mobile-energy/mobile-energy.component.ts